/**
 * Skeleton — Crop Circle loading placeholder primitive.
 *
 * A simple animated shimmer block for use in loading states. Matches the
 * design system convention: rounded, neutral-200 base color with a subtle
 * shimmer animation via CSS on web.
 *
 * Text cells in Gripp must use styled Crop Circle <Text> to avoid the
 * Paper default (Times New Roman on web). This component never renders bare
 * text, so no wrapping is needed here, but consumers that render text inside
 * a skeleton layout should follow the same rule.
 */
import React from 'react';
import { Animated, Easing, Platform, View, ViewStyle } from 'react-native';
import { colors, spacing, borderRadius } from '../crop-circle/tokens';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export interface SkeletonProps {
  /** Width of the skeleton block. Can be a number (px) or '100%' etc. */
  width?: number | string;
  /** Height of the skeleton block. Defaults to 16. */
  height?: number;
  /** Border radius override. Defaults to tokens.borderRadius.sm. */
  radius?: number;
  /** Additional style overrides for the outer View. */
  style?: ViewStyle;
  /** Test ID for testing. */
  testID?: string;
}

/**
 * Single shimmer block. Animates opacity on native; uses a CSS animation on web.
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = 16,
  radius,
  style,
  testID,
}) => {
  const anim = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    if (Platform.OS === 'web') return; // CSS handles it on web
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(anim, {
          toValue: 1,
          duration: 900,
          easing: Easing.ease,
          useNativeDriver: true,
        }),
        Animated.timing(anim, {
          toValue: 0,
          duration: 900,
          easing: Easing.ease,
          useNativeDriver: true,
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [anim]);

  const opacity =
    Platform.OS === 'web'
      ? 1
      : anim.interpolate({ inputRange: [0, 1], outputRange: [0.4, 0.9] });

  const resolvedRadius = radius ?? borderRadius.sm;

  return (
    <Animated.View
      testID={testID}
      // On web the shimmer is driven by a CSS keyframe targeting
      // [data-gripp-skeleton]; RNW maps `dataSet` -> `data-*` attributes, so
      // this is what actually makes the animation apply. Without it the block
      // renders static. Native uses the Animated opacity loop above instead.
      {...(Platform.OS === 'web'
        ? { dataSet: { grippSkeleton: 'true' } }
        : null)}
      style={[
        styles.base,
        {
          width: width as ViewStyle['width'],
          height,
          borderRadius: resolvedRadius,
          opacity,
        },
        Platform.OS === 'web' && (styles.webShimmer as ViewStyle),
        style,
      ]}
    />
  );
};

/**
 * SkeletonGroup — stacks several Skeleton rows with consistent gap.
 * Convenience wrapper for multi-line loading placeholders.
 */
export interface SkeletonGroupProps {
  /** Number of rows to render. Defaults to 3. */
  rows?: number;
  /** Height of each row. Defaults to 16. */
  rowHeight?: number;
  /** Gap between rows. Defaults to tokens.spacing.sm. */
  gap?: number;
  style?: ViewStyle;
  testID?: string;
}

export const SkeletonGroup: React.FC<SkeletonGroupProps> = ({
  rows = 3,
  rowHeight = 16,
  gap = spacing.sm,
  style,
  testID,
}) => (
  <View style={[{ gap }, style]} testID={testID}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton
        key={i}
        height={rowHeight}
        // Vary widths for a more natural look
        width={i === rows - 1 ? '60%' : '100%'}
      />
    ))}
  </View>
);

const styles = themedStyleSheet((role) => ({
  base: {
    backgroundColor: role.surfaceActive,
  },
  // On web, override via inline style object (StyleSheet.create can't hold CSS strings)
  webShimmer: {},
}));

// Inject the CSS shimmer animation on web once (module-level side effect).
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const STYLE_ID = '__gripp_skeleton_shimmer__';
  if (!document.getElementById(STYLE_ID)) {
    const styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    styleEl.textContent = `
      @keyframes gripp-skeleton-shimmer {
        0%   { opacity: 0.4; }
        50%  { opacity: 0.9; }
        100% { opacity: 0.4; }
      }
      [data-gripp-skeleton] {
        animation: gripp-skeleton-shimmer 1.8s ease-in-out infinite;
      }
    `;
    document.head.appendChild(styleEl);
  }
}
